/**
 * Insert Secret Field Button Component
 * Toolbar button to insert a secret field into the editor.
 *
 * Flow:
 *  1. Ensure master password is configured and unlocked (existing modal flow).
 *  2. If note is unsaved (noteId === null), auto-save it first.
 *  3. If note key wrapper already exists, insert the field immediately.
 *  4. Otherwise open NoteKeyWrapperProgressModal to create the wrapper,
 *     then insert the field on success.
 */

import React, { useState, useRef } from 'react';
import { usePublisher } from '@mdxeditor/editor';
import { insertSecretField$ } from '../plugins/secretFieldPlugin';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import { useNoteContext } from '@/components/notes/context/NoteContext';
import { useNoteKeyStore } from '@/store/noteKeyStore';
import { useManageNote } from '@/api/notes/useManageNote';
import NoteKeyWrapperProgressModal from '@/components/secrets/NoteKeyWrapperProgressModal';
import { triggerErrorMessage } from '@/components/ui/toast';

const InsertSecretFieldButton: React.FC = () => {
  const insertSecretField = usePublisher(insertSecretField$);

  // Master password store
  const isConfigured    = useMasterPasswordStore((s) => s.isConfigured);
  const masterKey       = useMasterPasswordStore((s) => s.masterKey);
  const openModal       = useMasterPasswordStore((s) => s.openModal);
  const openUnlockModal = useMasterPasswordStore((s) => s.openUnlockModal);
  // Always-fresh store getter — safe to call inside pending action callbacks
  const getMasterKey    = () => useMasterPasswordStore.getState().masterKey;

  // Note context
  const { noteId, hasKeyWrapper, setHasKeyWrapper } = useNoteContext();
  // Ref so pending action callbacks always read the latest context values
  const noteIdRef        = useRef(noteId);
  const hasKeyWrapperRef = useRef(hasKeyWrapper);
  noteIdRef.current        = noteId;
  hasKeyWrapperRef.current = hasKeyWrapper;

  // Note key store
  const setNoteKey = useNoteKeyStore((s) => s.setNoteKey);

  // Note management (for auto-save)
  const { updateOrCreateNote } = useManageNote();

  // Progress modal state
  const [wrapperModalOpen, setWrapperModalOpen] = useState(false);
  // Store masterKey at the moment the modal is opened so it is stable for the modal's lifetime
  const [modalMasterKey, setModalMasterKey] = useState<Uint8Array | null>(null);
  const [modalNoteId, setModalNoteId]       = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Insert action (always called after all prerequisites are met)
  // -------------------------------------------------------------------------

  const doInsert = () => {
    insertSecretField({ label: '', content: '', fieldType: 'inline' });
  };

  // -------------------------------------------------------------------------
  // Main flow — called once master key is confirmed in memory
  // -------------------------------------------------------------------------

  const proceedWithMasterKey = async () => {
    // Read fresh values — this function may be called as a pending action
    // after the unlock/setup modal closes, at which point the React state
    // closure would still hold the pre-unlock null value.
    const currentMasterKey = getMasterKey();
    if (!currentMasterKey) return;

    // Step 1: ensure the note is persisted (has a server-side UUID)
    let resolvedNoteId = noteIdRef.current;
    if (!resolvedNoteId) {
      try {
        const saved = await updateOrCreateNote(null, { title: '', content: '' });
        resolvedNoteId = saved?.id ?? null;
      } catch {
        triggerErrorMessage('Failed to save the note before adding a secret field.');
        return;
      }
      if (!resolvedNoteId) {
        triggerErrorMessage('Could not obtain a note ID. Please try again.');
        return;
      }
    }

    // Step 2: if wrapper already exists, insert immediately
    if (hasKeyWrapperRef.current) {
      doInsert();
      return;
    }

    // Step 3: open the progress modal with stable values captured now
    setModalMasterKey(currentMasterKey);
    setModalNoteId(resolvedNoteId);
    setWrapperModalOpen(true);
  };

  // -------------------------------------------------------------------------
  // Button click handler
  // -------------------------------------------------------------------------

  const handleClick = () => {
    if (!isConfigured) {
      // Master password not set up yet — open setup modal, retry on success
      openModal(() => { void proceedWithMasterKey(); });
      return;
    }

    if (!masterKey) {
      // Configured but key not in memory — prompt unlock, retry on success
      openUnlockModal(() => { void proceedWithMasterKey(); });
      return;
    }

    void proceedWithMasterKey();
  };

  // -------------------------------------------------------------------------
  // Progress modal callbacks
  // -------------------------------------------------------------------------

  const handleWrapperSuccess = (noteKey: Uint8Array) => {
    setWrapperModalOpen(false);
    setHasKeyWrapper(true);

    // Store the live note key if it's the real one (non-empty = fresh key from
    // a new wrapper; empty Uint8Array signals a 409 / pre-existing wrapper).
    if (modalNoteId && noteKey.byteLength > 0) {
      setNoteKey(modalNoteId, noteKey);
    }

    doInsert();
  };

  const handleWrapperCancel = () => {
    setWrapperModalOpen(false);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="p-2 rounded transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
        title="Insert Secret Field"
        aria-label="Insert Secret Field"
      >
        {/* Lock icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="inline-block"
        >
          <path d="M12 17a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0-2 2 2 2 0 0 0 2 2m6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V6a5 5 0 0 1 5-5 5 5 0 0 1 5 5v2h1m-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3z" />
        </svg>
      </button>

      {wrapperModalOpen && modalMasterKey && modalNoteId && (
        <NoteKeyWrapperProgressModal
          isOpen={wrapperModalOpen}
          noteId={modalNoteId}
          masterKey={modalMasterKey}
          onSuccess={handleWrapperSuccess}
          onCancel={handleWrapperCancel}
        />
      )}
    </>
  );
};

export default InsertSecretFieldButton;
